// src/HelpPage.js
import React, { useState } from 'react';
import Footer from './Footer';

const HelpPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      // Using Netlify Forms
      const formPayload = new FormData();
      formPayload.append('form-name', 'contact');
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('subject', formData.subject);
      formPayload.append('message', formData.message);

      await fetch('/', {
        method: 'POST',
        body: formPayload
      });

      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please email us directly at ngfmcontacts@gmail.com');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-4">
            <a href="/" className="text-white hover:text-green-100">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </a>
            <h1 className="text-3xl font-bold">Help & Support</h1>
          </div>
          <p className="text-green-100">
            Learn how to use Naija Petrol Prices and claim your station
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Quick Links */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">Quick Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="#how-to-use" className="p-4 border-2 border-green-600 rounded-lg hover:bg-green-50 transition-colors">
              <div className="text-2xl mb-2">📱</div>
              <div className="font-semibold text-green-700">How to Use</div>
              <div className="text-sm text-gray-600">Find fuel prices near you</div>
            </a>
            <a href="#claim-station" className="p-4 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="text-2xl mb-2">🏪</div>
              <div className="font-semibold text-blue-700">Claim Your Station</div>
              <div className="text-sm text-gray-600">Station owners start here</div>
            </a>
            <a href="#contact" className="p-4 border-2 border-purple-600 rounded-lg hover:bg-purple-50 transition-colors">
              <div className="text-2xl mb-2">💬</div>
              <div className="font-semibold text-purple-700">Contact Support</div>
              <div className="text-sm text-gray-600">Get help from our team</div>
            </a>
          </div>
        </div>

        {/* How to Use Section */}
        <div id="how-to-use" className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <span className="text-3xl">📱</span>
            How to Use Naija Petrol Prices
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">1. Find Fuel Prices</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Select your state from the dropdown menu</li>
                <li>Choose fuel type: PMS (Petrol), AGO (Diesel), DPK (Kerosene), or LPG (Cooking Gas)</li>
                <li>Browse stations or search for specific ones</li>
                <li>View prices, availability, and queue status</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">2. Update Prices</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Click "Update Price & Availability" on any station</li>
                <li>Enter current price you observed</li>
                <li>Select availability status</li>
                <li>Add your name (optional but recommended)</li>
                <li>Submit - your update is live immediately!</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">3. Add New Stations</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Click "Add New Station" button</li>
                <li>Fill in station details</li>
                <li>Capture GPS location for map accuracy</li>
                <li>Add current fuel prices</li>
                <li>Submit - helps the community!</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Station Owner Section */}
        <div id="claim-station" className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <span className="text-3xl">🏪</span>
            For Station Owners: Claim Your Station
          </h2>

          <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
            <p className="font-semibold text-blue-900">Why Claim Your Station?</p>
            <ul className="list-disc list-inside text-blue-800 mt-2 space-y-1">
              <li>Update prices instantly from anywhere</li>
              <li>Your updates show with a verified "Owner" badge</li>
              <li>Manage your station across all devices</li>
              <li>Build trust with customers</li>
            </ul>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                Get Your Owner Code
              </h3>
              <div className="ml-8 text-gray-700">
                <p className="mb-2">Contact us to get your unique 8-character owner code:</p>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <p className="font-semibold mb-2">📧 Email: <a href="mailto:ngfmcontacts@gmail.com" className="text-blue-600 hover:underline">ngfmcontacts@gmail.com</a></p>
                  <p className="text-sm text-gray-600">Include:</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 ml-4">
                    <li>Your station name</li>
                    <li>Station address</li>
                    <li>Your contact information</li>
                    <li>Proof of ownership (if requested)</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  We'll verify your ownership and send you a unique code within 24-48 hours.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                Create Your Account
              </h3>
              <div className="ml-8 text-gray-700">
                <ol className="list-decimal list-inside space-y-2">
                  <li>Go to the <a href="/owner" className="text-blue-600 hover:underline font-semibold">Owner Portal</a></li>
                  <li>Click "Sign in with Google"</li>
                  <li>Use your Google account (Gmail, business email, etc.)</li>
                  <li>You'll be redirected to your owner dashboard</li>
                </ol>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                Claim Your Station
              </h3>
              <div className="ml-8 text-gray-700">
                <ol className="list-decimal list-inside space-y-2">
                  <li>In the owner dashboard, click "Claim a Station"</li>
                  <li>Enter your 8-character owner code (e.g., ABC12XYZ)</li>
                  <li>Click "Claim Station"</li>
                  <li>✅ Your station is now linked to your account!</li>
                </ol>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                Manage Your Station
              </h3>
              <div className="ml-8 text-gray-700">
                <p className="mb-2">Once claimed, you can:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Update prices instantly</strong> - Click "Update Prices" anytime</li>
                  <li><strong>Change availability</strong> - Mark fuels as available, limited, or out of stock</li>
                  <li><strong>Update queue status</strong> - Let customers know wait times</li>
                  <li><strong>Access from anywhere</strong> - Phone, tablet, computer - all synced</li>
                </ul>
                <p className="mt-3 text-sm bg-green-50 border border-green-200 rounded p-3">
                  💡 <strong>Tip:</strong> Your updates show with a green "✓ by Owner" badge, so customers know the price is official!
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="font-semibold text-yellow-900 mb-2">🔐 Security Note:</p>
            <p className="text-sm text-yellow-800">
              Keep your owner code confidential. It's like a password for your station. 
              If you lose it, contact support at <a href="mailto:ngfmcontacts@gmail.com" className="underline">ngfmcontacts@gmail.com</a>
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Q: Are prices updated in real-time?</h3>
              <p className="text-gray-700">A: Yes! All price updates appear instantly for all users. The platform uses Firebase for real-time synchronization.</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Q: Can I claim multiple stations?</h3>
              <p className="text-gray-700">A: Yes! If you own multiple stations, request codes for each one. You'll manage them all from a single owner account.</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Q: What if someone updates wrong prices?</h3>
              <p className="text-gray-700">A: As a station owner, you can override any public update. Your updates show with a verified badge and take priority.</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Q: Is there a mobile app?</h3>
              <p className="text-gray-700">A: The website is mobile-optimized and works perfectly on all devices. You can add it to your home screen for app-like experience.</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Q: How do I report incorrect information?</h3>
              <p className="text-gray-700">A: Use the contact form below or email ngfmcontacts@gmail.com with details. We'll investigate and take appropriate action.</p>
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div id="contact" className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <span className="text-3xl">💬</span>
            Contact Support
          </h2>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-gray-700 mb-2">
              <strong>Email us directly:</strong> <a href="mailto:ngfmcontacts@gmail.com" className="text-blue-600 hover:underline font-semibold">ngfmcontacts@gmail.com</a>
            </p>
            <p className="text-sm text-gray-600">
              We typically respond within 24 hours. For urgent issues, please mention "URGENT" in your subject line.
            </p>
          </div>

          {submitted ? (
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-xl font-bold text-green-900 mb-2">Message Sent Successfully!</h3>
              <p className="text-green-800 mb-4">
                Thank you for contacting us. We'll respond to your email within 24 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" name="contact" method="POST" data-netlify="true">
              <input type="hidden" name="form-name" value="contact" />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Your Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Your Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className="w-full border rounded-lg px-4 py-2"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subject *</label>
                <select
                  required
                  value={formData.subject}
                  onChange={(e) => updateField('subject', e.target.value)}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="">Select a subject</option>
                  <option value="Request Owner Code">Request Owner Code</option>
                  <option value="Technical Issue">Technical Issue</option>
                  <option value="Report Incorrect Data">Report Incorrect Data</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Feature Suggestion">Feature Suggestion</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message *</label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => updateField('message', e.target.value)}
                  className="w-full border rounded-lg px-4 py-2"
                  rows="6"
                  placeholder="Tell us how we can help..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={sending}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Message
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default HelpPage;
